import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Tooltip,
  FormControlLabel,
  Switch,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search,
  PlayArrow,
  Pause,
  CheckCircle,
  Warning,
  MonitorHeart,
  Favorite,
  HourglassBottom,
  HeartBroken,
  ErrorOutline,
  MoreVert,
  Visibility,
  DeleteOutline,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePlayerHealth, useRemovePlayerDevice } from '../../hooks/useFleet';
import type { PlayerHealthDevice, PlayerStatus } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { fleetHelp } from '../../components/PageHelp/helpContent';
import { FLEET_TABS, FLEET_ACCENT_COLOR } from './fleetTabs';
import { useOrgFilter } from '../../contexts/OrganizationContext';
import {
  PLAYER_STATUS_COLORS,
  PLAYER_STATUS_LABELS,
  formatSecondsAgo,
  shortVersion,
  trackLabel,
  describeFailure,
  venueCaption,
} from './playerHealthFormat';

const STATUS_VALUES: PlayerStatus[] = ['alive', 'stale', 'dead'];

function isStatus(value: string | null): value is PlayerStatus {
  return !!value && (STATUS_VALUES as string[]).includes(value);
}

export default function PlayerHealth() {
  const navigate = useNavigate();
  const orgFilter = useOrgFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const statusFilter: PlayerStatus | '' = isStatus(statusParam) ? statusParam : '';
  const orgParam = searchParams.get('organization_id') || undefined;
  const siteParam = searchParams.get('site_id') || undefined;
  const zoneParam = searchParams.get('zone_id') || undefined;
  const scopeParam = orgParam || siteParam || zoneParam;
  // 'errors' = players with errors in the last 24h, 'requirements' = players
  // below the minimum device requirements. Set by the summary cards.
  const focusParam = searchParams.get('focus');
  const focus: 'errors' | 'requirements' | '' =
    focusParam === 'errors' || focusParam === 'requirements' ? focusParam : '';
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showInactive, setShowInactive] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuDevice, setMenuDevice] = useState<PlayerHealthDevice | null>(null);
  const removeDevice = useRemovePlayerDevice();

  // The status filter is applied client-side so the summary cards keep
  // counting the whole fleet while the table narrows.
  // Deep links from the Organizations / Sites / Zones status dots narrow the
  // fetch itself so the summary cards describe the scoped group.
  const { data, isLoading, error, dataUpdatedAt, isFetching } = usePlayerHealth({
    organization_id: orgParam || orgFilter,
    site_id: siteParam,
    zone_id: zoneParam,
    include_inactive: showInactive || undefined,
  });

  const devices: PlayerHealthDevice[] = useMemo(() => data?.data || [], [data]);
  const summary = data?.summary || { total: 0, alive: 0, stale: 0, dead: 0 };
  const errors24h = useMemo(() => devices.reduce((n, d) => n + (d.errors_last_24h || 0), 0), [devices]);
  const belowRequirements = useMemo(
    () => devices.filter((d) => d.meets_requirements === false).length,
    [devices]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return devices.filter((d) => {
      if (statusFilter && d.status !== statusFilter) return false;
      if (focus === 'errors' && !(d.errors_last_24h > 0)) return false;
      if (focus === 'requirements' && d.meets_requirements !== false) return false;
      if (!q) return true;
      return [
        d.zone_name,
        d.site_name,
        d.organization_name,
        d.user_email,
        d.user_name,
        d.device_id,
        d.app_version,
        d.current_track_title,
        d.current_track_artist,
      ].some((v) => v && v.toLowerCase().includes(q));
    });
  }, [devices, search, statusFilter, focus]);

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const scopeParams = (): Record<string, string> => {
    const out: Record<string, string> = {};
    if (orgParam) out.organization_id = orgParam;
    if (siteParam) out.site_id = siteParam;
    if (zoneParam) out.zone_id = zoneParam;
    return out;
  };

  const setStatus = (value: string) => {
    setPage(0);
    const next: Record<string, string> = { ...scopeParams() };
    if (value) next.status = value;
    if (focus) next.focus = focus;
    setSearchParams(next);
  };

  // Toggle: clicking the active card clears it.
  const setFocus = (value: 'errors' | 'requirements') => {
    setPage(0);
    const next: Record<string, string> = { ...scopeParams() };
    if (statusFilter) next.status = statusFilter;
    if (focus !== value) next.focus = value;
    setSearchParams(next);
  };

  const clearScope = () => {
    setPage(0);
    const next: Record<string, string> = {};
    if (statusFilter) next.status = statusFilter;
    if (focus) next.focus = focus;
    setSearchParams(next);
  };

  const scopeLabel = (() => {
    if (!scopeParam) return '';
    const first = devices[0];
    if (zoneParam) return first?.zone_name ? `Zone: ${first.zone_name}` : 'Zone';
    if (siteParam) return first?.site_name ? `Site: ${first.site_name}` : 'Site';
    return first?.organization_name ? `Organization: ${first.organization_name}` : 'Organization';
  })();

  const updatedSecondsAgo = dataUpdatedAt ? Math.round((Date.now() - dataUpdatedAt) / 1000) : null;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, device: PlayerHealthDevice) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuDevice(device);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuDevice(null);
  };
  const handleRemove = async () => {
    const device = menuDevice;
    handleMenuClose();
    if (!device) return;
    const label = device.zone_name || device.site_name || device.device_id.slice(0, 8);
    if (!window.confirm(`Remove player "${label}" (${device.user_email || 'unknown user'})? Its history is deleted. If the install reports again it will re-register.`)) return;
    try {
      await removeDevice.mutateAsync(device.device_id);
    } catch (err) {
      console.error('Failed to remove player device:', err);
      window.alert(`Failed to remove player: ${(err as Error)?.message || 'Unknown error'}`);
    }
  };

  const stats: Array<{
    title: string;
    value: number;
    icon: React.ReactNode;
    active: boolean;
    onClick: () => void;
  }> = [
    { title: 'Players', value: summary.total, icon: <MonitorHeart sx={{ color: FLEET_ACCENT_COLOR }} />, active: false, onClick: () => { setPage(0); setSearchParams(scopeParams()); } },
    { title: 'Alive', value: summary.alive, icon: <Favorite sx={{ color: PLAYER_STATUS_COLORS.alive.color }} />, active: statusFilter === 'alive', onClick: () => setStatus(statusFilter === 'alive' ? '' : 'alive') },
    { title: 'Stale', value: summary.stale, icon: <HourglassBottom sx={{ color: PLAYER_STATUS_COLORS.stale.color }} />, active: statusFilter === 'stale', onClick: () => setStatus(statusFilter === 'stale' ? '' : 'stale') },
    { title: 'Dead', value: summary.dead, icon: <HeartBroken sx={{ color: PLAYER_STATUS_COLORS.dead.color }} />, active: statusFilter === 'dead', onClick: () => setStatus(statusFilter === 'dead' ? '' : 'dead') },
    { title: 'Errors (24h)', value: errors24h, icon: <ErrorOutline sx={{ color: '#ef4444' }} />, active: focus === 'errors', onClick: () => setFocus('errors') },
    { title: 'Below requirements', value: belowRequirements, icon: <Warning sx={{ color: '#f59e0b' }} />, active: focus === 'requirements', onClick: () => setFocus('requirements') },
  ];

  if (error) {
    return (
      <Box>
        <SectionTabs title="Fleet Management" tabs={FLEET_TABS} accentColor={FLEET_ACCENT_COLOR} help={fleetHelp.playerHealth} />
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load player health: {(error as Error)?.message || 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <SectionTabs
        title="Fleet Management"
        tabs={FLEET_TABS}
        accentColor={FLEET_ACCENT_COLOR}
        help={fleetHelp.playerHealth}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => {
          return (
            <Grid item xs={6} sm={4} md={2} key={stat.title}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  outline: stat.active ? `2px solid ${FLEET_ACCENT_COLOR}` : 'none',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                }}
                onClick={stat.onClick}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ mb: 1 }}>{stat.icon}</Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {isLoading ? '-' : stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search zone, site, user, version..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUS_VALUES.map((s) => (
                <MenuItem key={s} value={s}>
                  {PLAYER_STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {scopeParam && (
            <Chip label={scopeLabel} onDelete={clearScope} size="small" sx={{ bgcolor: `${FLEET_ACCENT_COLOR}20`, color: FLEET_ACCENT_COLOR }} />
          )}
          {focus && (
            <Chip
              label={focus === 'errors' ? 'With errors (24h)' : 'Below requirements'}
              onDelete={() => setFocus(focus)}
              size="small"
              sx={{ bgcolor: focus === 'errors' ? '#ef444420' : '#f59e0b20', color: focus === 'errors' ? '#ef4444' : '#f59e0b' }}
            />
          )}
          <Tooltip title="Players not seen for 7 days or more are hidden by default and do not count toward the status dots">
            <FormControlLabel
              control={<Switch size="small" checked={showInactive} onChange={(e) => { setShowInactive(e.target.checked); setPage(0); }} />}
              label={
                <Typography variant="body2">
                  Show inactive{!showInactive && summary.inactive_hidden ? ` (${summary.inactive_hidden} hidden)` : ''}
                </Typography>
              }
            />
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {isFetching ? 'Refreshing...' : updatedSecondsAgo !== null ? `Updated ${formatSecondsAgo(updatedSecondsAgo).toLowerCase()}` : ''}
            {' '}(auto-refresh 30s)
          </Typography>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Zone</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Now playing</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Requirements</TableCell>
                <TableCell>Last seen</TableCell>
                <TableCell align="right">Errors (24h)</TableCell>
                <TableCell width={60}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {devices.length === 0
                        ? 'No player has reported yet'
                        : focus === 'errors'
                        ? 'No player has reported an error in the last 24 hours'
                        : focus === 'requirements'
                        ? 'Every player meets the minimum device requirements'
                        : 'No players match the current filter'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((device) => {
                  const colors = PLAYER_STATUS_COLORS[device.status];
                  const track = trackLabel(device);
                  return (
                    <TableRow
                      key={device.device_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/fleet/player-health/${device.device_id}`)}
                    >
                      <TableCell>
                        <Chip
                          label={PLAYER_STATUS_LABELS[device.status]}
                          size="small"
                          sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                          {device.zone_name || <span style={{ opacity: 0.6 }}>No zone selected</span>}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 320 }}>
                          {venueCaption(device) || device.device_id.slice(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{device.user_email || '-'}</Typography>
                        {device.display_mode && (
                          <Typography variant="caption" color="text.secondary">
                            {device.display_mode}
                            {device.engine ? ` / ${device.engine}` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {device.live ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {device.is_playing ? (
                              <PlayArrow fontSize="small" sx={{ color: '#10b981' }} />
                            ) : (
                              <Pause fontSize="small" sx={{ color: '#6b7280' }} />
                            )}
                            <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                              {track || (device.is_playing ? 'Playing' : 'Paused')}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No live snapshot
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {shortVersion(device.app_version)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {device.meets_requirements === null ? (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        ) : device.meets_requirements ? (
                          <CheckCircle fontSize="small" sx={{ color: '#10b981' }} />
                        ) : (
                          <Tooltip
                            title={
                              <Box>
                                {device.requirement_failures.map((f) => (
                                  <div key={f}>{describeFailure(f)}</div>
                                ))}
                              </Box>
                            }
                          >
                            <Warning fontSize="small" sx={{ color: '#f59e0b' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatSecondsAgo(device.last_seen_seconds_ago)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {device.errors_last_24h > 0 ? (
                          <Tooltip title="Open the error timeline">
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ fontWeight: 700, color: '#ef4444', textDecoration: 'underline', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/fleet/player-health/${device.device_id}?events=error`);
                              }}
                            >
                              {device.errors_last_24h}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2">0</Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, device)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            const id = menuDevice?.device_id;
            handleMenuClose();
            if (id) navigate(`/fleet/player-health/${id}`);
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRemove} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteOutline fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Remove Player</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
